import { useState, useEffect, useRef } from 'react';
import {
  UserKeys,
  getAllGroupsForUser,
  getUsersDirectorySearchUserPortal,
  getUserPortalUser,
} from '../../api/users-api';
import { User } from '../../../types';
import {
  UserViewRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { Link } from 'react-router-dom';
import useDebounce from '../../utils/useDebounce';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useUserSettings } from '../../utils/use-user-settings';
import { Messages } from '../../utils/message-constants';
import { userPortalPagesConfig } from '../../shared/user-portal-pages-config';
import UserAvatar from '../../shared/user-avatar';
import { pagesConfig } from '../../shared/pages-config';
import StatusLabel from '../../shared/status-label';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListSubheader from '@mui/material/ListSubheader';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Group from '@mui/icons-material/Group';
import GroupRemove from '@mui/icons-material/GroupRemove';
import Person from '@mui/icons-material/Person';
import Phone from '@mui/icons-material/Phone';
import SharedTabHeader from '../../shared/shared-tab-header';
export default function DirectorySearchPage() {
  const { primaryDomain, userViewRoles } = useUserSettings();
  const { createEvent } = useAnalytics();

  const [selectedUser, setSelectedUser] = useState<string>('');
  const previousSelectedUser = useRef<string>('');

  const [searchText, setSearchText] = useState('');
  const debouncedInput = useDebounce(searchText, 200);
  const [ref, inView] = useInView();

  useEffect(() => {
    if (selectedUser !== previousSelectedUser.current) {
      createEvent(analytics.userView.directory_search.viewed_user, {
        module: ModuleList.modules.userView.name,
        section: ModuleList.modules.userView.sections.directorySearch,
      });

      previousSelectedUser.current = selectedUser;
    }
  }, [selectedUser, createEvent]);

  const { fetchNextPage, isFetchingNextPage, hasNextPage, data, isLoading } =
    useInfiniteQuery({
      queryKey: [UserKeys.UserPortalDirectorySearch, debouncedInput],
      queryFn: ({ pageParam }) => {
        return getUsersDirectorySearchUserPortal(
          {
            results: [],
            firstPull: false,
            searchTime: 0,
            pageSize: 50,
            cursor: pageParam,
            nameSpace: '',
          },
          debouncedInput
        );
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => lastPage.cursor,
      meta: {
        errorMessage: Messages.Errors.Network.Loading('user data'),
      },
    });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: [UserKeys.User, selectedUser],
    queryFn: () => getUserPortalUser(selectedUser),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('user data'),
    },
    enabled: selectedUser !== '',
  });

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: [UserKeys.UserGroups, selectedUser],
    queryFn: () => getAllGroupsForUser(selectedUser),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group data'),
    },
    enabled: selectedUser !== '',
  });

  function createStatusChip(user: User | undefined) {
    if (!user) {
      return null;
    }

    const status = user.isArchived
      ? 'ARCHIVED'
      : user.suspended
      ? 'SUSPENDED'
      : 'ACTIVE';

    return <StatusLabel status={status} />;
  }

  function createOU(user: User | undefined) {
    if (!user) {
      return null;
    }

    if (user.orgUnitPath === '/') {
      return primaryDomain;
    } else {
      return user.orgUnitPath.split('/').slice(-1)[0];
    }
  }

  let primaryPhone = userData?.phones?.find((number) => number.primary);
  if (!primaryPhone && userData?.phones) {
    primaryPhone = userData.phones[0];
  }
  let primaryOrg = userData?.organizations?.find((org) => org.primary);
  if (!primaryOrg && userData?.organizations) {
    primaryOrg = userData.organizations[0];
  }
  let manager = userData?.relations?.find(
    (relation) => relation.type === 'manager'
  );
  if (!manager && userData?.relations) {
    manager = userData.relations[0];
  }

  return (
    <Box sx={{ p: 2, pt: 1 }}>
      <SharedTabHeader title="Directory Search" noDivider />
      <Box
        sx={{
          minHeight: 300,
          height: '63vh',
          width: '95%',
          mx: 'auto',
        }}
      >
        <TextField
          inputProps={{
            id: 'user-search',
          }}
          InputLabelProps={{
            htmlFor: 'user-search',
          }}
          label="Search for a user"
          sx={{ width: 350 }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
        <Stack flexDirection="row" py={1} gap={3}>
          <Card
            sx={{
              maxHeight: '55vh',
              width: '50%',
              minWidth: 350,
            }}
          >
            <List style={{ overflowY: 'auto', maxHeight: '55vh', padding: 0 }}>
              <ListSubheader
                disableGutters
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ p: 1 }}>
                  All Available Users
                </Typography>
                <ListItemSecondaryAction
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {isLoading && <CircularProgress size={21} />}
                </ListItemSecondaryAction>
              </ListSubheader>
              {data?.pages
                ?.flatMap((user) => user.results)
                .map((user) => {
                  return (
                    <ListItemButton
                      key={user.userEmail}
                      selected={selectedUser === user.userEmail}
                      onClick={() => {
                        setSelectedUser(user.userEmail);
                      }}
                    >
                      <ListItemAvatar>
                        <UserAvatar
                          userEmail={user.userEmail}
                          isUserPortal={true}
                        />
                      </ListItemAvatar>
                      <Typography
                        noWrap
                        sx={{
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.userEmail}
                      </Typography>
                    </ListItemButton>
                  );
                })}
              {data?.pages?.flatMap((user) => user.results).length === 0 ? (
                <Stack justifyContent="center" alignItems="center">
                  <Typography sx={{ p: 1 }}>No users found</Typography>
                </Stack>
              ) : null}

              {!hasNextPage || isFetchingNextPage ? null : (
                <Stack justifyContent="center" alignItems="center">
                  <Button ref={ref} onClick={() => fetchNextPage()}>
                    {hasNextPage ? 'Fetch More Data' : 'No more data'}
                  </Button>
                </Stack>
              )}

              {isFetchingNextPage ? (
                <ListItem style={{ justifyContent: 'center' }}>
                  <Skeleton variant="rounded" width={'100%'} height={30} />
                  <Skeleton variant="rounded" width={'100%'} height={30} />
                </ListItem>
              ) : null}
            </List>
          </Card>
          <Card
            sx={{
              height: '55vh',
              width: '100%',
            }}
          >
            {selectedUser === '' ? (
              <Stack
                height={'100%'}
                alignItems="center"
                justifyContent="center"
              >
                <Stack flexDirection="row" alignItems="center">
                  <Person />
                  <Typography variant="h5">Search or select a user</Typography>
                </Stack>
                <Typography variant="body1">
                  Here you can view the details of a searched or selected user.
                </Typography>
              </Stack>
            ) : isLoadingUser || isLoadingGroups ? (
              <Stack
                height={'100%'}
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Stack>
            ) : (
              <Stack gap={1} pl={2} pt={4}>
                <Stack flexDirection={'row'} gap={4}>
                  <Box pt={1}>
                    <UserAvatar
                      userEmail={userData?.primaryEmail || ''}
                      isUserPortal={true}
                      sx={{ width: 100, height: 100 }}
                    />
                  </Box>
                  <Stack>
                    <Typography sx={{ fontWeight: 400, fontSize: 20 }}>
                      {`${
                        userData?.name?.givenName
                          ? userData?.name?.givenName
                          : ' '
                      } ${
                        userData?.name?.familyName
                          ? userData?.name?.familyName
                          : ' '
                      }`}
                    </Typography>
                    <Typography sx={{ fontWeight: 300, fontSize: 15 }}>
                      {userData?.primaryEmail}
                    </Typography>
                    <Stack flexDirection="row" gap={1} alignItems="center">
                      {createStatusChip(userData)}
                      <Divider orientation="vertical" flexItem />
                      <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                        <Tooltip title={'Org Unit'}>
                          <span>{createOU(userData)}</span>
                        </Tooltip>
                      </Typography>
                    </Stack>
                    <Stack flexDirection={'row'} gap={1}>
                      {primaryOrg &&
                      primaryOrg.name &&
                      primaryOrg.name.length > 0 ? (
                        <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                          {primaryOrg.name}
                        </Typography>
                      ) : null}
                      {primaryOrg &&
                      primaryOrg.department &&
                      primaryOrg.department.length > 0 ? (
                        <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                          {primaryOrg.department}
                        </Typography>
                      ) : null}
                      {primaryOrg &&
                      primaryOrg.title &&
                      primaryOrg.title.length > 0 ? (
                        <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                          {primaryOrg.title}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Stack flexDirection={'row'} gap={1}>
                      {primaryOrg &&
                      primaryOrg.costCenter &&
                      primaryOrg.costCenter.length > 0 ? (
                        <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                          {primaryOrg.costCenter}
                        </Typography>
                      ) : null}
                      {primaryOrg &&
                      primaryOrg.location &&
                      primaryOrg.location.length > 0 ? (
                        <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                          {primaryOrg.location}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
                {(primaryPhone && primaryPhone.type === 'work') || manager ? (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Stack gap={1}>
                      <Typography sx={{ fontWeight: 500, fontSize: 20 }}>
                        Contact Information:
                      </Typography>
                      {primaryPhone ? (
                        <Stack
                          flexDirection={'row'}
                          gap={1}
                          alignItems={'center'}
                        >
                          <Typography sx={{ fontWeight: 300, fontSize: 15 }}>
                            Work:
                          </Typography>
                          <Chip
                            key={primaryPhone.value}
                            label={primaryPhone.value.replace(/\./g, '-')}
                            size="medium"
                            avatar={<Avatar children={<Phone />} />}
                          />
                        </Stack>
                      ) : null}
                      {manager ? (
                        <Stack
                          flexDirection={'row'}
                          gap={1}
                          alignItems={'center'}
                        >
                          <Typography sx={{ fontWeight: 300, fontSize: 15 }}>
                            Manager:
                          </Typography>
                          <Chip
                            key={manager.value}
                            label={manager.value}
                            size="medium"
                            avatar={<Avatar children={<Person />} />}
                          />
                        </Stack>
                      ) : null}
                    </Stack>
                  </>
                ) : null}
                <Divider orientation="vertical" flexItem />
                <Stack gap={1}>
                  <Typography sx={{ fontWeight: 500, fontSize: 20 }}>
                    Groups:
                  </Typography>
                  {groupsData && groupsData.length > 0 ? (
                    <Stack flexDirection="row" gap={1} flexWrap={'wrap'}>
                      {groupsData?.map((group) => (
                        <Chip
                          clickable={isAbleToSeeUIElement(
                            userViewRoles,
                            UserViewRoles.ViewAllGroups.value
                          )}
                          component={Link}
                          to={
                            isAbleToSeeUIElement(
                              userViewRoles,
                              UserViewRoles.ViewAllGroups.value
                            )
                              ? `../${userPortalPagesConfig.userPortalAllGroups.link}/${pagesConfig.groups.link}/${group.email}`
                              : ''
                          }
                          key={group.email}
                          label={group.email}
                          size="medium"
                          avatar={<Avatar children={<Group />} />}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Stack flexDirection="row" alignItems="center" gap={2}>
                      <GroupRemove />
                      <Typography variant="h6">No Groups Found</Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}
